import { useEffect, useState } from 'react';
import { useApiService } from '../services/apiService';
import { sendPasswordReset } from '../auth/embeddedLogin';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';

interface PasswordAccount { canChangePassword: boolean; email?: string; connection?: string }

export function ProfilePassword() {
  const api = useApiService();
  const [account, setAccount] = useState<PasswordAccount>();
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setError('');
    api.get<PasswordAccount>('/profile/password').then(value => { if (!cancelled) setAccount(value); })
      .catch(() => { if (!cancelled) setError('Unable to load password settings. Try again.'); });
    return () => { cancelled = true; };
  }, [api, revision]);
  const change = async () => {
    if (busy || !account?.canChangePassword || !account.email || !account.connection) return;
    setBusy(true); setError('');
    try { await sendPasswordReset(account.email, account.connection); setSent(true); }
    catch { setError('Unable to send a password-change link. Please wait and try again.'); }
    finally { setBusy(false); }
  };
  return <Card data-analytics-private="true" className="mp-block">
    <CardHeader><CardTitle>Password</CardTitle><CardDescription>Manage your sign-in password.</CardDescription></CardHeader>
    <CardContent className="space-y-3">
      {!account && !error && <p role="status">Loading password settings…</p>}
      {error && <p role="alert" className="text-destructive">{error}</p>}
      {!account && error && <Button variant="outline" onClick={() => setRevision(v => v + 1)}>Retry password settings</Button>}
      {account?.canChangePassword && <>
        <p className="text-sm text-muted-foreground">We’ll email a secure link to your account email. Follow it to choose your new password, then sign in again.</p>
        <Button variant="outline" disabled={busy || sent} onClick={() => void change()}>{busy ? 'Sending link…' : 'Change password'}</Button>
      </>}
      {account && !account.canChangePassword && <p>Your password is managed by your identity provider. Change it through your organization or sign-in provider.</p>}
      {sent && <p role="status">Password-change link requested. Check your inbox and spam folder, and use the latest email.</p>}
    </CardContent>
  </Card>;
}
